import { FC, HTMLAttributes } from "react";
import Image from "next/image";
import clsx from "clsx";

import SectionHeader from "@/sections/common/SectionHeader";

// ----------------------------------------------------------------------------
// CertificatesSection — Certbar's own audit & compliance credentials.
//
// Minimal layout to match the site vibe: clean badge row, no card frames,
// no boxes, no shadows. Each badge stands on its own with the cert name
// below it as a small uppercase label. Hover is a quiet 1px lift +
// opacity bump — no glow, no ring.
//
// Heading + eyebrow + subtitle flow through SectionHeader so the type
// scale matches every other section we've redesigned.
// ----------------------------------------------------------------------------

export type Icertificate = {
  logo: string;
  heading: string;
};

export type ICertificatesSectionProps = HTMLAttributes<HTMLElement> & {
  eyebrow?: string;
  heading?: string;
  subtitle?: string;
  certificates?: Icertificate[];
};

const CertificatesSection: FC<ICertificatesSectionProps> = (props) => {
  const {
    eyebrow = "Audited & certified",
    heading,
    subtitle,
    certificates,
    className,
    ...other
  } = props;

  // Drop any cert without a logo URL — a broken Strapi reference was
  // rendering an empty image cell on the live page.
  const validCertificates = (certificates ?? []).filter(
    (c) => typeof c.logo === "string" && c.logo.trim().length > 0,
  );

  if (validCertificates.length === 0) return null;

  return (
    <section
      className={clsx(
        "container mx-auto py-10 md:py-14 lg:py-16 xl:py-20",
        className,
      )}
      {...other}
    >
      <SectionHeader
        eyebrow={eyebrow}
        title={heading}
        subtitle={subtitle}
        className="mb-10 md:mb-12 lg:mb-14"
      />

      {/* Responsive badge row.
            mobile (<sm)  → 2 cols grid (badges large enough to read)
            sm (640+)     → 3 cols grid
            md (768+)     → flex-wrap, badges sit on one row with extra space
            lg+ (1024+)   → flex-wrap, more breathing room
          Switching from grid → flex at md gives a centered, naturally
          wrapping row when there's plenty of horizontal space, but keeps
          tight rows of evenly-sized cells on small screens (avoids the
          "one badge alone on the last row" awkwardness that flex-wrap
          can produce on narrow viewports). */}
      <ul className="mx-auto grid max-w-5xl list-none grid-cols-2 items-start justify-center gap-x-6 gap-y-10 sm:grid-cols-3 sm:gap-x-8 md:flex md:flex-wrap md:gap-x-12 md:gap-y-12 lg:gap-x-16">
        {validCertificates.map((certificate, idx) => (
          <li
            key={idx}
            className="group flex flex-col items-center gap-4 transition-transform duration-300 hover:-translate-y-0.5"
          >
            {/* Image cell — bumped from h-16/20/24 to h-24/28/32.
                Uses clamp via responsive Tailwind so the badge still
                shrinks gracefully on the very narrowest viewports. */}
            <div className="relative flex h-24 w-24 items-center justify-center md:h-28 md:w-28 lg:h-32 lg:w-32 xl:h-36 xl:w-36">
              <Image
                src={certificate.logo}
                alt={certificate.heading}
                fill
                sizes="(max-width: 768px) 96px, (max-width: 1024px) 112px, (max-width: 1280px) 128px, 144px"
                className="object-contain opacity-95 transition-opacity duration-300 group-hover:opacity-100"
              />
            </div>

            {/* Label — bumped from 10-12px font-medium uppercase
                (read as tiny footnote) to 14-16px font-semibold normal-case
                (reads as a real caption). Removed tight tracking +
                uppercase so the cert names ("ISO/IEC 27018:2019") fit
                on one line without truncation on most viewports. */}
            <p className="max-w-[10rem] text-center font-bricolage-grotesque text-[clamp(0.875rem,0.3vw+0.8rem,1rem)] font-semibold leading-snug text-black-800 transition-colors duration-300 group-hover:text-primary-900 md:max-w-[12rem]">
              {certificate.heading}
            </p>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default CertificatesSection;
